import React, {ReactNode, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import ChatProductSlider, {CardRef} from "./ChatProductSlider";
import ChatProductLink from "./ChatProductLink";
import ChatProductBrowser from "./ChatProductBrowser";
import {ChatListedInterface} from "../../interfaces/customer/ChatInterface";
import useChatProducts from "./useChatProducts";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import {inStockFirst} from "./chatProductFilter";

// Ссылки в ответах консультанта — на страницы сайта (/catalog/item/…, /catalog/list/…):
// такие же маршруты есть в b2b, открываем их здесь. Чужие ссылки показываем текстом — текст пришёл от модели
function internalPath(url: string): string | null {
    if (/^\/(?!\/)/.test(url)) {
        return url;
    }
    const match = url.match(/^https:\/\/(?:www\.)?pride\.ru(\/.*)$/);

    return match ? match[1] : null;
}

// Карточка товара: /catalog/item/{раздел}/{id}/{slug}
function productId(url: string): number | null {
    const path = internalPath(url);
    const match = path ? path.match(/^\/catalog\/item\/[^/\s)]+\/(\d+)\//) : null;

    return match ? Number(match[1]) : null;
}

const INLINE = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|(\/catalog\/item\/[^\s)]+)/g;

// Страницы сайта, которых нет в b2b: PDF инструкции (как «скачать инструкцию» в TechDoc), «Где купить» с картой,
// переход в интернет-магазин на поиск товара (/where-to-buy/shop/{id}?q=… — редирект сайта)
const SITE_ONLY = /^\/(?:public\/files\/|where-to-buy(?:[?#]|$)|where-to-buy\/shop\/\d+(?:[?#]|$))/;

// Ссылка на товар открывает его в модальном окне, страницы только сайта — pride.ru в новой вкладке,
// остальные ссылки сайта — переход внутри b2b
function link(path: string, label: ReactNode, key: number): ReactNode {
    if (SITE_ONLY.test(path)) {
        return <a key={key} className="chat-link" href={`https://www.pride.ru${path}`} target="_blank" rel="noopener noreferrer">{label}</a>;
    }
    const id = productId(path);

    return id ? <ChatProductLink key={key} productId={id} href={path}>{label}</ChatProductLink> : <Link key={key} className="chat-link" to={path}>{label}</Link>;
}

// Внутри жирного бывает ссылка («**Только у [JAI-1056](/catalog/item/…):**» в сравнении) — разбираем его рекурсивно,
// поэтому у каждого вызова свой экземпляр регулярки (lastIndex)
function inline(text: string): ReactNode[] {
    const nodes: ReactNode[] = [];
    const pattern = new RegExp(INLINE);
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > last) {
            nodes.push(text.slice(last, match.index));
        }
        const key = nodes.length;
        if (match[1] !== undefined) {
            const path = internalPath(match[2]);
            nodes.push(path ? link(path, match[1], key) : match[1]);
        } else if (match[3] !== undefined) {
            nodes.push(<strong key={key}>{inline(match[3])}</strong>);
        } else {
            nodes.push(link(match[4], match[4], key));
        }
        last = match.index + match[0].length;
    }
    if (last < text.length) {
        nodes.push(text.slice(last));
    }

    return nodes;
}

// Пункт с вложенными — строка-переключатель («**ЭТМ** — 9 точек продаж ▾»), вложенные показываются по клику
function ChatListItem({item}: {item: ListItem}) {
    const [open, setOpen] = useState(false);
    if (!item.children.length) {
        return <li>{inline(item.text)}</li>;
    }

    return <li>
        <span className="chat-toggle" role="button" tabIndex={0} aria-expanded={open}
              onClick={() => setOpen(!open)}
              onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && (event.preventDefault(), setOpen(!open))}>
            {inline(item.text)}<span className="chat-toggle__caret">{open ? '▴' : '▾'}</span>
        </span>
        {open && <ul className="ps-3 mb-1">{item.children.map((child, index) => <li key={index}>{inline(child)}</li>)}</ul>}
    </li>;
}

// Длинный список (партнёры в «где купить»: в Москве их 36) — первые LIST_PAGE и «Показать ещё»;
// до LIST_LIMIT пунктов показываем целиком (интернет-магазины — 11 строк)
const LIST_PAGE = 10;
const LIST_LIMIT = 15;

function ChatList({items}: {items: ListItem[]}) {
    const [visible, setVisible] = useState(items.length > LIST_LIMIT ? LIST_PAGE : items.length);
    const rest = items.length - visible;

    return <>
        <ul className="mb-2 ps-4">{items.slice(0, visible).map((item, index) => <ChatListItem key={index} item={item}/>)}</ul>
        {rest > 0 && <button type="button" className="btn btn-sm gray chat-list-more mb-2" onClick={() => setVisible(visible + LIST_PAGE)}>
            Показать ещё {Math.min(LIST_PAGE, rest)} из {rest}
        </button>}
    </>;
}

// Маленькая картинка товара над артикулом в шапке таблицы сравнения; пока товар не пришёл из каталога — ничего
function TableProductImage({card, products}: {card: CardRef | null, products: Record<number, ProductInterface>}) {
    const product = card ? products[card.id] : undefined;
    if (!card || !product) {
        return null;
    }

    return <ChatProductLink productId={card.id} href={card.href}>
        <img className="assistant__table-image" src={'https://www.pride.ru' + (product.list_image || '/images/no-photo.jpg')} alt={product.name}/>
    </ChatProductLink>;
}

function cells(row: string): string[] {
    return row.replace(/^\||\|$/g, '').split('|').map(cell => cell.trim());
}

// Пункт списка; children — вложенные пункты («  - …»): точки продаж партнёра в «где купить», свёрнуты до клика
interface ListItem {
    text: string
    children: string[]
}

type Block =
    | {type: 'paragraph', lines: string[]}
    | {type: 'heading', text: string} // «### Обязательно» в подборке под задачу
    | {type: 'list', items: ListItem[]}
    | {type: 'table', rows: string[]}
    | {type: 'cards', cards: CardRef[]}
    | {type: 'browser', cards: CardRef[]} // список поиска целиком: «Показать ещё» и фильтры

// Пункт списка, который целиком — ссылка на товар («- [Название](/catalog/item/…)»): список поиска и подсказки
function cardItem(item: string): CardRef | null {
    const match = item.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    const id = match ? productId(match[2]) : null;

    return match && id ? {id, href: internalPath(match[2]) as string, label: match[1]} : null;
}

/**
 * Разбор ответа на блоки. С карточками (withCards):
 * - список, где каждый пункт — ссылка на товар, становится мини-карточками;
 * - перед таблицей сравнения (в шапке ссылки на два и больше товаров) — карточки сравниваемых товаров;
 * - товары, на которые ответ ссылается в тексте, — карточками в конце ответа.
 * Товар, уже показанный карточкой в прошлых ответах диалога (shownBefore), второй раз карточкой не идёт —
 * остаётся ссылкой (пункт списка — пунктом списка).
 * browse — у ответа есть весь найденный список (поиск, похожие): первый список товаров становится ChatProductBrowser
 * целиком, без оглядки на shownBefore — это новый отбор, который покупатель листает и фильтрует.
 */
function parse(text: string, withCards: boolean, shownBefore: ReadonlySet<number> = new Set(), browse = false): Block[] {
    const blocks: Block[] = [];
    let paragraph: string[] = [];
    let list: ListItem[] = [];
    let cards: CardRef[] = [];
    let table: string[] = [];
    let footerText = '';

    const flushParagraph = () => {
        // Подвал списка поиска («Показаны 15 из 1133. Отбор: … Разделы: [..](/catalog/list/…)») при карточках не нужен:
        // сколько нашлось, говорит фраза перед списком. В тексте ответа он остаётся — по нему бот узнаёт список в истории
        const footer = withCards && ['cards', 'list', 'browser'].includes(blocks[blocks.length - 1]?.type) && /Разделы: \[[^\]]+\]\(\/catalog\/list\//.test(paragraph.join(' '));
        if (footer) footerText += paragraph.join('\n');
        if (paragraph.length && !footer) blocks.push({type: 'paragraph', lines: paragraph});
        paragraph = [];
    };
    const flushList = () => {
        if (list.length) blocks.push({type: 'list', items: list});
        list = [];
    };
    // Подряд идущие новые товары — карточками, уже показанные — ссылками, порядок списка сохраняется
    let browsing = browse;
    const flushCards = () => {
        if (browsing && cards.length) {
            blocks.push({type: 'browser', cards});
            browsing = false;
            cards = [];
            return;
        }
        cards.forEach(card => {
            const fresh = !shownBefore.has(card.id);
            const last = blocks[blocks.length - 1];
            if (fresh && last?.type === 'cards') {
                last.cards.push(card);
            } else if (fresh) {
                blocks.push({type: 'cards', cards: [card]});
            } else if (last?.type === 'list') {
                last.items.push({text: `[${card.label}](${card.href})`, children: []});
            } else {
                blocks.push({type: 'list', items: [{text: `[${card.label}](${card.href})`, children: []}]});
            }
        });
        cards = [];
    };
    const flushTable = () => {
        const rows = table.filter(row => !/^\|[\s\-:|]+\|$/.test(row));
        if (rows.length && withCards) {
            const compared: CardRef[] = [];
            cells(rows[0]).forEach(cell => {
                const card = cardItem(cell);
                if (card) compared.push(card);
            });
            const fresh = compared.filter(card => !shownBefore.has(card.id));
            if (compared.length >= 2 && fresh.length) blocks.push({type: 'cards', cards: fresh});
        }
        if (rows.length) blocks.push({type: 'table', rows});
        table = [];
    };

    text.split('\n').forEach(line => {
        const trimmed = line.trim();
        if (/^\|.*\|$/.test(trimmed)) {
            flushParagraph();
            flushList();
            flushCards();
            table.push(trimmed);
            return;
        }
        flushTable();
        // Вложенный пункт — к последнему пункту списка, карточкой не становится
        const nested = line.match(/^\s{2,}[-*•]\s+(.*)$/);
        if (nested && list.length) {
            list[list.length - 1].children.push(nested[1].trim());
            return;
        }
        const item = trimmed.match(/^[-*•]\s+(.*)$/);
        const card = item && withCards ? cardItem(item[1]) : null;
        if (card) {
            flushParagraph();
            flushList();
            cards.push(card);
            return;
        }
        flushCards();
        if (item) {
            flushParagraph();
            list.push({text: item[1], children: []});
            return;
        }
        flushList();
        const heading = trimmed.match(/^#{1,6}\s+(.+)$/);
        if (heading) {
            flushParagraph();
            blocks.push({type: 'heading', text: heading[1]});
            return;
        }
        if (trimmed === '') {
            flushParagraph();
        } else {
            paragraph.push(trimmed);
        }
    });
    flushTable();
    flushList();
    flushCards();
    flushParagraph();

    if (withCards && !text.startsWith('Подбор похожих товаров: ')) {
        // Ссылка на источник в вопросе о свойствах остаётся ссылкой: карточка после списка мешает выбору.
        // Товары, упомянутые в тексте ссылкой, — карточками в конце (без тех, что уже показаны карточками
        // и без скрытого подвала: «Похожие на [S03A8124](…)» — не упоминание)
        const shown = new Set<number>(shownBefore);
        blocks.forEach(block => (block.type === 'cards' || block.type === 'browser') && block.cards.forEach(card => shown.add(card.id)));
        const mentioned: CardRef[] = [];
        const links = /\[([^\]]+)\]\(([^)\s]+)\)|((?:https:\/\/(?:www\.)?pride\.ru)?\/catalog\/item\/[^\s)]+)/g;
        let match: RegExpExecArray | null;
        const visible = footerText ? text.replace(footerText, '') : text;
        while ((match = links.exec(visible)) !== null) {
            const url = match[2] ?? match[3];
            const id = productId(url);
            if (id && !shown.has(id)) {
                shown.add(id);
                mentioned.push({id, href: internalPath(url) as string, label: match[1] ?? url});
            }
        }
        if (mentioned.length) blocks.push({type: 'cards', cards: mentioned});
    }

    return blocks;
}

// Уточняющий вопрос подбора под задачу (TaskAdvisor на сайте): первая строка «Подбор под задачу «…»»,
// в конце — варианты ответа пунктами, ссылок на товары нет. Набор товаров по задаче так же начинается, но со ссылками
const TASK_PREFIX = 'Подбор под задачу «';

/**
 * Варианты ответа, если ответ бота — уточняющий вопрос подбора; иначе null. Выбор отправляется текстом варианта:
 * сайт сверяет его с вариантами вопроса и засчитывает без разбора моделью.
 */
export function taskChoices(text: string): string[] | null {
    const lines = text.trim().split('\n').map(line => line.trim());
    const similar = lines[0]?.startsWith('Подбор похожих товаров: ');
    if (!similar && (!lines[0]?.startsWith(TASK_PREFIX) || /\/catalog\/item\//.test(text))) {
        return null;
    }
    const choices: string[] = [];
    for (let index = lines.length - 1; index > 0; index--) {
        const item = lines[index].match(/^[-*•]\s+(.+)$/);
        if (!item) break;
        choices.unshift(item[1]);
    }

    // В бинарном вопросе положительный ответ всегда первым; отправляем исходный текст.
    const binary = choices.map(choice => choice.toLocaleLowerCase('ru').replace(/[.!?]+$/, '').trim());
    if (choices.length === 2 && binary.includes('да') && binary.includes('нет')) {
        return binary[0] === 'да' ? choices : [choices[1], choices[0]];
    }

    return choices.length ? choices : null;
}

// Свой вариант отправляется только после подтверждения; при множественном выборе — вместе с отмеченными.
function ChatChoices({choices, multiple, allowOther = true, onChoose}: {choices: string[], multiple: boolean, allowOther?: boolean, onChoose: (choice: string) => void}) {
    const [selected, setSelected] = useState<string[]>([]);
    const [other, setOther] = useState(false);
    const [custom, setCustom] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const values = choices.filter(choice => selected.includes(choice));
    if (other && custom.trim()) values.push(custom.trim());
    const canSubmit = !submitted && values.length > 0 && (!other || !!custom.trim());
    const choose = (answer: string) => {
        if (submitted) return;
        setSubmitted(true);
        onChoose(answer);
    };
    return <div className="chat-choices mb-2">
        {choices.map(choice => multiple ? <label key={choice} className="chat-choice">
            <input type="checkbox" checked={selected.includes(choice)} disabled={submitted}
                   style={{flex: '0 0 16px', width: 16, height: 16, marginTop: 2, accentColor: '#ff5c35'}}
                   onChange={event => setSelected(event.target.checked
                       ? [...selected, choice] : selected.filter(value => value !== choice))}/>
            <span className="chat-choice__text"><span className="chat-choice__label">{choice}</span></span>
        </label> : <button key={choice} type="button" className="chat-choice" disabled={submitted} onClick={() => choose(choice)}>
            <span className="chat-choice__circle" aria-hidden="true"/>
            <span className="chat-choice__text">
                <span className="chat-choice__label">{choice.split(' — ')[0]}</span>
                {choice.includes(' — ') && <span className="chat-choice__description"> — {choice.slice(choice.indexOf(' — ') + 3)}</span>}
            </span>
        </button>)}
        {allowOther && <div className="d-flex flex-wrap align-items-start gap-2">
            {multiple ? <label className="chat-choice">
                <input type="checkbox" checked={other} disabled={submitted}
                       style={{flex: '0 0 16px', width: 16, height: 16, marginTop: 2, accentColor: '#ff5c35'}}
                       onChange={event => setOther(event.target.checked)}/>
                <span className="chat-choice__label">Другое</span>
            </label> : <button type="button" className="chat-choice" disabled={submitted}
                               aria-expanded={other} onClick={() => setOther(!other)}>
                <span className="chat-choice__circle" aria-hidden="true"/>
                <span className="chat-choice__label">Другое</span>
            </button>}
            {other && <textarea className="form-control" aria-label="Свой вариант" placeholder="Напишите свой вариант"
                                autoFocus rows={2} value={custom} disabled={submitted}
                                style={{flex: '1 1 240px', width: 'auto'}} onChange={event => setCustom(event.target.value)}/>}
        </div>}
        {(multiple || other) && <button type="button" className="btn btn-sm gray align-self-start mt-2" disabled={!canSubmit}
                onClick={() => {
                    if (canSubmit) choose(multiple ? 'Выбрано:\n- ' + values.join('\n- ') : custom.trim());
                }}>Продолжить</button>}
    </div>;
}

/**
 * Товары, которые ответ показывает карточками (без учёта прошлых ответов): чтобы следующие ответы диалога
 * их не повторяли.
 */
export function cardIds(text: string): number[] {
    const ids: number[] = [];
    parse(text, true).forEach(block => block.type === 'cards' && block.cards.forEach(card => ids.push(card.id)));

    return ids;
}

/**
 * Небольшое подмножество Markdown, которое отдаёт консультант: жирный, списки, таблицы сравнения, ссылки.
 * withCards — товары мини-карточками с ценой клиента и наличием (готовый ответ; пока ответ идёт — ссылками).
 * shownBefore — товары, уже показанные карточками в прошлых ответах диалога: у них только ссылка.
 * listed — весь найденный список ответа (поиск, похожие): листается и фильтруется на странице.
 * onChoose — ответ можно выбрать кнопкой (последний ответ, бот свободен): варианты уточняющего вопроса подбора
 * (taskChoices) показываются кликабельными строками, нажатие отправляет текст варианта как вопрос.
 */
export default function ChatMarkdown({text, withCards = false, shownBefore, listed, onChoose}: {
    text: string
    withCards?: boolean
    shownBefore?: ReadonlySet<number>
    listed?: ChatListedInterface
    onChoose?: (choice: string) => void
}) {
    const blocks = useMemo(() => parse(text, withCards, shownBefore, withCards && !!listed?.ids.length), [text, withCards, shownBefore, listed]);
    const orderedChoices = useMemo(() => taskChoices(text), [text]);
    const choices = onChoose ? orderedChoices : null;
    const ids = useMemo(() => {
        const unique = new Set<number>();
        blocks.forEach(block => {
            if (block.type === 'cards') block.cards.forEach(card => unique.add(card.id));
            // Товары шапки таблицы сравнения — ради картинок в первой строке (карточками их может и не быть: уже показаны)
            if (block.type === 'table') cells(block.rows[0]).forEach(cell => {
                const card = cardItem(cell);
                if (card) unique.add(card.id);
            });
        });
        return Array.from(unique);
    }, [blocks]);
    const {products, loading} = useChatProducts(ids, withCards);

    return <>{blocks.map((block, index) => {
        switch (block.type) {
            case 'paragraph':
                // Тема нужна серверу для продолжения цепочки; покупателю показываем сам вопрос.
                if (block.lines[0]?.startsWith(TASK_PREFIX)) {
                    // В результате сразу показываем товары, без повторения темы и ответов.
                    if (!orderedChoices) {
                        return block.lines.length > 1 ? <p key={index} className="mb-2">{block.lines.slice(1).map((line, lineIndex) => (
                            <React.Fragment key={lineIndex}>{lineIndex > 0 && <br/>}{inline(line)}</React.Fragment>
                        ))}</p> : null;
                    }
                    const marker = ' Уже знаю: ';
                    const known = block.lines[0].indexOf(marker);
                    return <React.Fragment key={index}>
                        {known !== -1 && <p className="chat-task-known mb-2">Выбрано: {block.lines[0].slice(known + marker.length)}</p>}
                        {block.lines.length > 1 && <p className="mb-2">{block.lines.slice(1).map((line, lineIndex) => (
                            <React.Fragment key={lineIndex}>{lineIndex > 0 && <br/>}{inline(line)}</React.Fragment>
                        ))}</p>}
                    </React.Fragment>;
                }
                return <p key={index} className="mb-2">{block.lines.map((line, lineIndex) => (
                    <React.Fragment key={lineIndex}>{lineIndex > 0 && <br/>}{line.startsWith(TASK_PREFIX) && line.includes(' Уже знаю: ')
                        ? <>{inline(line.slice(0, line.indexOf(' Уже знаю: ')))}<span className="chat-task-known">Выбрано: {line.slice(line.indexOf(' Уже знаю: ') + ' Уже знаю: '.length)}</span></>
                        : inline(line)}</React.Fragment>
                ))}</p>;
            case 'list':
                // Варианты вопроса подбора — компактные строки с немедленной отправкой ответа
                if (choices && index === blocks.length - 1) {
                    const similar = text.startsWith('Подбор похожих товаров: ');
                    const multiple = similar || text.split('\n').some(line => line.trim().startsWith('Можно выбрать несколько вариантов'));
                    return <ChatChoices key={text} choices={choices} multiple={multiple} allowOther={!similar} onChoose={onChoose!}/>;
                }
                return <ChatList key={index} items={orderedChoices && index === blocks.length - 1
                    ? orderedChoices.map(choice => block.items.find(item => item.text === choice) as ListItem)
                    : block.items}/>;
            case 'heading':
                return <div key={index} className="chat-heading">{inline(block.text)}</div>;
            case 'cards':
                if (text.startsWith(TASK_PREFIX)) {
                    return <ChatProductBrowser key={index} listed={{ids: block.cards.map(card => card.id), total: block.cards.length}}
                                               cards={block.cards} pageSize={12}/>;
                }
                // Сначала в наличии — когда товары из каталога пришли; до того порядок текста
                return <ChatProductSlider key={index} cards={inStockFirst(block.cards, card => products[card.id])} products={products} loading={loading}/>;
            case 'browser':
                return <ChatProductBrowser key={index} listed={listed as ChatListedInterface} cards={block.cards}
                                           pageSize={text.includes('Похожие на ') ? 12 : undefined}/>;
            default: {
                const [head, ...body] = block.rows;
                return <div key={index} className="table-responsive mb-2">
                    <table className="table table-sm table-bordered mb-0 assistant__table">
                        <thead><tr>{cells(head).map((cell, cellIndex) => <th key={cellIndex}>
                            <TableProductImage card={withCards ? cardItem(cell) : null} products={products}/>
                            {inline(cell)}
                        </th>)}</tr></thead>
                        <tbody>{body.map((row, rowIndex) => (
                            <tr key={rowIndex}>{cells(row).map((cell, cellIndex) => <td key={cellIndex}>{inline(cell)}</td>)}</tr>
                        ))}</tbody>
                    </table>
                </div>;
            }
        }
    })}</>;
}
